import { useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Column {
  key: string;
  label: string;
  render?: (item: any) => ReactNode;
  width?: string;
  minWidth?: string;
  collapsible?: boolean; // Se a coluna pode ser ocultada
}

interface ImprovedTableProps {
  columns: Column[];
  data: any[];
  keyField?: string;
  striped?: boolean;
  hoverable?: boolean;
  compactable?: boolean; // Se permite compactar/expandir colunas
  defaultCollapsed?: string[]; // Colunas colapsadas por padrão
}

const ImprovedTable: React.FC<ImprovedTableProps> = ({
  columns,
  data,
  keyField = 'id',
  striped = true,
  hoverable = true,
  compactable = true,
  defaultCollapsed = [],
}) => {
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(
    new Set(defaultCollapsed)
  );

  const toggleColumn = (columnKey: string) => {
    setCollapsedColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  const visibleColumns = columns.filter(
    (col) => !collapsedColumns.has(col.key)
  );

  const tableStyles: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  };

  const headerRowStyles: React.CSSProperties = {
    backgroundColor: '#f8f9fa',
    borderBottom: '2px solid #dee2e6',
  };

  const headerCellStyles: React.CSSProperties = {
    padding: '14px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#495057',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  };

  const getRowStyles = (index: number): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      borderBottom: '1px solid #e9ecef',
      transition: 'all 0.2s ease',
    };

    // Striped rows
    if (striped && index % 2 === 0) {
      baseStyles.backgroundColor = '#f8f9fa';
    } else {
      baseStyles.backgroundColor = '#ffffff';
    }

    return baseStyles;
  };

  const cellStyles: React.CSSProperties = {
    padding: '14px 16px',
    color: '#212529',
  };

  const hoverStyles: React.CSSProperties = hoverable
    ? {
        cursor: 'pointer',
        backgroundColor: '#e7f3ff !important',
        transform: 'scale(1.001)',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      }
    : {};

  return (
    <div style={{ position: 'relative' }}>
      {/* Controles de colunas (se compactable) */}
      {compactable && columns.some((col) => col.collapsible) && (
        <div
          style={{
            marginBottom: '12px',
            padding: '8px 12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
            fontSize: '12px',
          }}
        >
          <span style={{ fontWeight: '600', color: '#495057', marginRight: '8px' }}>
            Colunas:
          </span>
          {columns
            .filter((col) => col.collapsible)
            .map((col) => (
              <button
                key={col.key}
                onClick={() => toggleColumn(col.key)}
                style={{
                  padding: '4px 10px',
                  border: `1px solid ${collapsedColumns.has(col.key) ? '#6c757d' : '#dee2e6'}`,
                  borderRadius: '4px',
                  backgroundColor: collapsedColumns.has(col.key) ? '#6c757d' : '#ffffff',
                  color: collapsedColumns.has(col.key) ? '#ffffff' : '#495057',
                  cursor: 'pointer',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  if (!collapsedColumns.has(col.key)) {
                    e.currentTarget.style.backgroundColor = '#e9ecef';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!collapsedColumns.has(col.key)) {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                  }
                }}
              >
                {collapsedColumns.has(col.key) ? (
                  <>
                    <ChevronRight size={12} />
                    {col.label}
                  </>
                ) : (
                  <>
                    <ChevronLeft size={12} />
                    {col.label}
                  </>
                )}
              </button>
            ))}
        </div>
      )}

      <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #dee2e6' }}>
        <table style={tableStyles}>
          <thead>
            <tr style={headerRowStyles}>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  style={{
                    ...headerCellStyles,
                    width: column.width,
                    minWidth: column.minWidth || '120px',
                  }}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr
                key={item[keyField]}
                style={getRowStyles(index)}
                onMouseEnter={(e) => {
                  if (hoverable) {
                    Object.assign(e.currentTarget.style, hoverStyles);
                  }
                }}
                onMouseLeave={(e) => {
                  if (hoverable) {
                    const baseStyles = getRowStyles(index);
                    e.currentTarget.style.backgroundColor = baseStyles.backgroundColor || '#ffffff';
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                  }
                }}
              >
                {visibleColumns.map((column) => (
                  <td
                    key={column.key}
                    style={{
                      ...cellStyles,
                      width: column.width,
                      minWidth: column.minWidth,
                    }}
                  >
                    {column.render
                      ? column.render(item)
                      : item[column.key] || '-'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ImprovedTable;

